import { useSetChatroomStatusMutation } from '@pba/react-chat-core';
import { useCallback, useEffect } from 'react';

type P = {
  chatroomId: string;
  isAtEnd: boolean;
  unreadCount: number;
};

const inFlightMarkAsReadByChatroomId = new Map<string, AbortController>();

export const useMarkChatroomAsReadUseEffect = ({ chatroomId, isAtEnd, unreadCount }: P) => {
  const { mutate: setChatroomStatus } = useSetChatroomStatusMutation();

  const markAsReadWhenActiveAndAtEnd = useCallback(() => {
    if (isAtEnd && document.visibilityState === 'visible' && document.hasFocus() && unreadCount > 0) {
      inFlightMarkAsReadByChatroomId.get(chatroomId)?.abort();

      const abortController = new AbortController();
      inFlightMarkAsReadByChatroomId.set(chatroomId, abortController);

      setChatroomStatus(
        { chatroomId, signal: abortController.signal, status: 'read' },
        {
          onSettled(_data, _error, variables) {
            if (inFlightMarkAsReadByChatroomId.get(variables.chatroomId) === abortController) {
              inFlightMarkAsReadByChatroomId.delete(variables.chatroomId);
            }
          },
        },
      );
    }
  }, [chatroomId, isAtEnd, setChatroomStatus, unreadCount]);

  useEffect(() => {
    markAsReadWhenActiveAndAtEnd();

    document.addEventListener('visibilitychange', markAsReadWhenActiveAndAtEnd);
    window.addEventListener('focus', markAsReadWhenActiveAndAtEnd);

    return () => {
      document.removeEventListener('visibilitychange', markAsReadWhenActiveAndAtEnd);
      window.removeEventListener('focus', markAsReadWhenActiveAndAtEnd);
    };
  }, [markAsReadWhenActiveAndAtEnd]);
};
